import React, { useState } from "react";
import { Mail, Send, Instagram, Linkedin, ArrowUpRight } from "lucide-react";
import { resolveImage, api } from "../lib/api";
import { useToast } from "../hooks/use-toast";

const Contact = ({ site }) => {
  const { toast } = useToast();
  const chat = site?.chat_contact || {};
  const socials = site?.socials || {};
  const voxel = site?.voxel_assets || {};
  const [msg, setMsg] = useState("");
  const [sender, setSender] = useState("");
  const [sending, setSending] = useState(false);

  const onSend = async () => {
    if (!msg.trim() || !sender.trim()) {
      toast({ title: "Almost there", description: "Please share your email and a short message." });
      return;
    }
    setSending(true);
    try {
      await api.sendMessage({
        name: sender.split("@")[0] || "Guest",
        email: sender.trim(),
        message: msg.trim(),
      });
      toast({ title: "Sent ✨", description: "I'll reply within a day." });
      setMsg("");
      setSender("");
    } catch (e) {
      toast({ title: "Couldn't send", description: e.message || "Try again in a moment." });
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative py-20 md:py-28"
      style={{
        background:
          "linear-gradient(180deg, var(--bg-2) 0%, color-mix(in oklab, var(--peach) 30%, var(--bg-2)) 100%)",
      }}
    >
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        {/* Centered heading + copy (unchanged) */}
        <div className="text-center max-w-3xl mx-auto">
          <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs bg-[color:var(--card)] border border-[color:var(--card-border)] text-[color:var(--ink)]">
            <Mail className="w-3.5 h-3.5 text-[color:var(--violet)]" /> {chat.eyebrow || "Contact"}
          </span>
          <h2
            data-testid="contact-heading"
            className="mt-6 font-display text-4xl md:text-6xl font-semibold leading-[1.02]"
          >
            {chat.heading || "Let's have chai?"}
          </h2>
          {chat.subtext && (
            <p className="mt-5 max-w-2xl mx-auto text-[color:var(--ink-soft)] leading-relaxed">
              {chat.subtext}
            </p>
          )}
        </div>

        {/* Two-column: LEFT chai-station scene · RIGHT compact chat */}
        <div className="mt-12 md:mt-16 grid md:grid-cols-12 gap-8 md:gap-10 items-center">
          {/* LEFT — chai station voxel */}
          <div className="md:col-span-6 flex justify-center md:justify-start" data-testid="contact-chai-station">
            {voxel.chai_station ? (
              <img
                src={resolveImage(voxel.chai_station)}
                alt="Voxel scene: Misbah and a friend having chai over their laptops."
                className="w-full max-w-[520px] h-auto mk-drift select-none pointer-events-none"
                draggable={false}
              />
            ) : (
              <div className="w-full max-w-[520px] aspect-square rounded-3xl bg-[color:var(--violet-soft)]" />
            )}
          </div>

          {/* RIGHT — compact chat mockup + inline form */}
          <div className="md:col-span-6">
            <div
              data-testid="contact-chat"
              className="rounded-[24px] bg-[color:var(--card)] border border-[color:var(--card-border)] shadow-sm p-5 md:p-6"
            >
              {/* Incoming bubble from Misbah */}
              <div className="flex items-end gap-3">
                {voxel.about ? (
                  <img
                    src={resolveImage(voxel.about)}
                    alt="Misbah"
                    className="w-10 h-10 rounded-full object-cover bg-[color:var(--bg-2)] border border-[color:var(--card-border)] shrink-0"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-[color:var(--violet)] shrink-0" />
                )}
                <div className="mk-bubble-in text-sm">
                  {chat.question || "Want to work together? Or just want to chat?"}
                </div>
              </div>

              {/* Outgoing reply */}
              <div className="mt-4 flex items-end gap-3 justify-end">
                <div className="mk-bubble-out text-sm">
                  {chat.reply_prefix || "Email me at:"}{" "}
                  <a href={`mailto:${site?.email}`} className="underline underline-offset-2">
                    {site?.email}
                  </a>
                </div>
                <div className="w-10 h-10 rounded-full bg-[color:var(--butter)] border border-[color:var(--card-border)] shrink-0 grid place-items-center font-display font-semibold">
                  {(site?.name || "M").slice(0, 1)}
                </div>
              </div>

              {/* Compact message box */}
              <div className="mt-5 rounded-2xl border border-[color:var(--card-border)] bg-[color:var(--bg-2)] p-3">
                <input
                  type="email"
                  value={sender}
                  onChange={(e) => setSender(e.target.value)}
                  placeholder="Your email"
                  data-testid="contact-email"
                  className="w-full bg-transparent outline-none py-1.5 px-2 text-sm placeholder:text-[color:var(--muted)]"
                />
                <div className="border-t border-[color:var(--card-border)] mt-1.5 pt-1.5 flex items-end gap-2">
                  <textarea
                    rows={2}
                    value={msg}
                    onChange={(e) => setMsg(e.target.value)}
                    placeholder="Say hi, share your idea, or ask anything…"
                    data-testid="contact-message"
                    className="flex-1 bg-transparent outline-none resize-none py-1.5 px-2 text-sm placeholder:text-[color:var(--muted)]"
                  />
                  <button
                    onClick={onSend}
                    disabled={sending}
                    data-testid="contact-send"
                    className="mk-btn mk-btn-violet disabled:opacity-60 !py-2 !px-3.5 text-sm"
                  >
                    <Send className="w-4 h-4" /> {sending ? "Sending…" : "Send"}
                  </button>
                </div>
              </div>
            </div>

            {/* Socials row */}
            <div className="mt-5 flex flex-wrap justify-center md:justify-start items-center gap-3">
              {socials.instagram && (
                <a
                  href={socials.instagram}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Instagram"
                  className="w-10 h-10 rounded-full border border-[color:var(--card-border)] grid place-items-center hover:bg-[color:var(--ink)] hover:text-[color:var(--bg)] transition-colors"
                >
                  <Instagram className="w-4 h-4" />
                </a>
              )}
              {socials.linkedin && (
                <a
                  href={socials.linkedin}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="LinkedIn"
                  className="w-10 h-10 rounded-full border border-[color:var(--card-border)] grid place-items-center hover:bg-[color:var(--ink)] hover:text-[color:var(--bg)] transition-colors"
                >
                  <Linkedin className="w-4 h-4" />
                </a>
              )}
              {socials.behance && (
                <a href={socials.behance} target="_blank" rel="noreferrer" className="text-sm mk-link">
                  Behance <ArrowUpRight className="inline w-3.5 h-3.5" />
                </a>
              )}
              {socials.dribbble && (
                <a href={socials.dribbble} target="_blank" rel="noreferrer" className="text-sm mk-link">
                  Dribbble <ArrowUpRight className="inline w-3.5 h-3.5" />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
